import { useEffect, useState, useMemo, useCallback } from 'react';
import { employeeApi, adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Select from '../../components/ui/Select.jsx';
import Input from '../../components/ui/Input.jsx';
import CalendarSubscribeCard from '../../components/CalendarSubscribeCard.jsx';
import { cn } from '@/lib/utils';
import {
  ChevronLeft, ChevronRight, Building2, Home, MapPin, Palmtree, Pencil, Plus,
  Save, Send, Check, AlertCircle, AlertTriangle, Loader2, X,
  Calendar as CalendarIcon, ChevronDown, HelpCircle,
} from 'lucide-react';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FALLBACK_COLORS = ['#3985FD', '#FC026F', '#8238EB', '#15D2A4', '#00AFDC', '#FFCA03', '#F85707', '#99CA32'];

export default function PlannerPage() {
  const toast = useToast();
  const { isAdmin } = useCurrentUser();
  const [showCategoriesEditor, setShowCategoriesEditor] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pending changes — persisted to localStorage so switching pages
  // (or reloading) doesn't wipe an unsaved plan. Cleared only on
  // Save or Discard.
  const DRAFT_KEY = 'prohr.planner.draft.v1';
  const [pendingSched, setPendingSched] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}').pendingSched || {}; } catch { return {}; }
  });
  const [removeSched, setRemoveSched] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}').removeSched || {}; } catch { return {}; }
  });
  const [pendingLeave, setPendingLeave] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}').pendingLeave || {}; } catch { return {}; }
  });

  // Persist draft whenever any pending bucket changes.
  useEffect(() => {
    const empty =
      Object.keys(pendingSched).length === 0 &&
      Object.keys(removeSched).length === 0 &&
      Object.keys(pendingLeave).length === 0;
    if (empty) {
      localStorage.removeItem(DRAFT_KEY);
    } else {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ pendingSched, removeSched, pendingLeave }));
      } catch { /* quota or disabled — not fatal */ }
    }
  }, [pendingSched, removeSched, pendingLeave]);

  // Tool
  const [selectedTool, setSelectedTool] = useState(''); // a work-location id or a leave-type id

  // Modals
  const [leaveModal, setLeaveModal] = useState(null);
  const [leaveForm, setLeaveForm] = useState({ typeId: '', startDate: '', endDate: '', reason: '' });
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutRequests, setCheckoutRequests] = useState([]);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Month calc. Pin to day 1 BEFORE shifting the month — otherwise a
  // setMonth call from e.g. "Apr 30" forwards to "Feb" overflows
  // ("Feb 30" → "Mar 2") and that month is silently skipped from the
  // navigation. (Bug: Feb 2027 wasn't appearing for users on the 30th
  // or 31st of any month.)
  const { year, month, monthLabel, daysInMonth, startDay } = useMemo(() => {
    const today = new Date();
    const d = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      monthLabel: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      daysInMonth: new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(),
      startDay: new Date(d.getFullYear(), d.getMonth(), 1).getDay(),
    };
  }, [monthOffset]);

  // Load data.
  //   - `clearPending` wipes the user's unsaved edits; only do that after a Save/Discard,
  //     not when switching months, so drafts survive navigation.
  //   - Subsequent loads use `refreshing` (inline indicator) instead of `loading`
  //     (full-page spinner) so the calendar doesn't flash blank on every month click.
  const reload = useCallback((opts = {}) => {
    const { clearPending = false, silent = false } = opts;
    if (silent || data) setRefreshing(true); else setLoading(true);
    const ms = String(month + 1).padStart(2, '0');
    const sd = `${year}-${ms}-01`;
    const ed = `${year}-${ms}-${String(daysInMonth).padStart(2, '0')}`;
    return employeeApi.getPlannerData(sd, ed)
      .then((res) => {
        setData(res);
        if (clearPending) {
          setPendingSched({});
          setRemoveSched({});
          setPendingLeave({});
        }
      })
      .catch((err) => toast.error(`Failed to load: ${err.message}`))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [year, month, daysInMonth, data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { reload(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = new Date();
  const todayStr = today.toISOString().substring(0, 10);
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();

  const holidaySet = useMemo(() => new Set((data?.holidays || []).map((h) => h.date)), [data?.holidays]);
  const holidayMap = useMemo(() => {
    const m = {};
    (data?.holidays || []).forEach((h) => { m[h.date] = h.name; });
    return m;
  }, [data?.holidays]);
  const workingDays = data?.workingDays || [0, 1, 2, 3, 4];

  // Admin-configurable work-location categories (Remote, Off Site, …) drive
  // the planner's instant, no-approval "where are you working" tools.
  const workLocations = useMemo(() => data?.workLocations || [], [data?.workLocations]);
  const activeLocations = useMemo(() => workLocations.filter((w) => w.active), [workLocations]);
  const wlById = useMemo(() => {
    const m = {};
    for (const w of workLocations) m[w.id] = w;
    return m;
  }, [workLocations]);
  const isWorkLocationId = useCallback((id) => activeLocations.some((w) => w.id === id), [activeLocations]);

  // Default the selected tool to the first work location once data loads.
  useEffect(() => {
    if (selectedTool) return;
    const first = activeLocations[0]?.id || (data?.leaveTypes || [])[0]?.rowKey || '';
    if (first) setSelectedTool(first);
  }, [activeLocations, data?.leaveTypes, selectedTool]);

  const isWorkDay = useCallback((ds) => {
    const dow = new Date(ds + 'T12:00:00').getDay();
    return workingDays.includes(dow) && !holidaySet.has(ds);
  }, [workingDays, holidaySet]);

  // A day is a holiday-locked day if it shows up in the Holidays table —
  // we don't want WFH or leave to land on a national/company holiday.
  const isHoliday = useCallback((ds) => holidaySet.has(ds), [holidaySet]);

  // KSA workweek is Sun-Thu by default. Fri/Sat are weekend days — the
  // backend's calculateWorkingDays already excludes them from the leave
  // balance count, but the UI should still let the user mark them
  // (e.g. WFH on a Friday) and visually include them in a leave range.
  const isWeekend = useCallback((ds) => {
    const dow = new Date(ds + 'T12:00:00').getDay();
    return !workingDays.includes(dow);
  }, [workingDays]);

  // "Plannable" = the user is allowed to interact with this day. We
  // gate click only on holidays (and past dates separately). Weekends
  // are plannable; they just don't burn leave balance.
  const isPlannable = useCallback((ds) => !isHoliday(ds), [isHoliday]);

  const getColor = useCallback((typeId) => {
    const types = data?.leaveTypes || [];
    const type = types.find((t) => t.rowKey === typeId);
    if (type?.color) return type.color;
    const idx = types.findIndex((t) => t.rowKey === typeId);
    return FALLBACK_COLORS[idx >= 0 ? idx % FALLBACK_COLORS.length : 0];
  }, [data?.leaveTypes]);

  const getTypeName = useCallback((typeId) => {
    return (data?.leaveTypes || []).find((t) => t.rowKey === typeId)?.displayName || typeId;
  }, [data?.leaveTypes]);

  // Day state
  const getDayInfo = useCallback((ds) => {
    if (!data) return { type: 'office' };
    // Holidays are locked — no click, no tool. Weekend days are NOT
    // locked anymore; they fall through to the same plan-state checks
    // as weekdays, just with a darker-grey background applied in render.
    if (isHoliday(ds)) return { type: 'holiday' };

    // Pending leave (unsaved)
    if (pendingLeave[ds]) return { type: 'pending-leave', typeId: pendingLeave[ds] };
    // Pending schedule (unsaved)
    if (pendingSched[ds]) return { type: `pending-${pendingSched[ds]}` };
    // Removed schedule — fall back to the day's resting state (office
    // for weekdays, weekend for Fri/Sat) so the X actually visually
    // clears the cell.
    if (removeSched[ds]) return { type: isWeekend(ds) ? 'weekend' : 'office' };

    // Existing leave from server
    const lr = (data.leaveRequests || []).find((r) => r.startDate <= ds && r.endDate >= ds);
    if (lr) return { type: 'existing-leave', typeId: lr.typeId, status: lr.status, typeName: lr.typeName };

    // Existing schedule from server
    const sched = data.schedule?.[ds];
    if (sched) return { type: `saved-${sched}` };

    return { type: isWeekend(ds) ? 'weekend' : 'office' };
  }, [data, pendingLeave, pendingSched, removeSched, isHoliday, isWeekend]);

  // Balance impact
  const balanceImpact = useMemo(() => {
    const impact = {};
    for (const [ds, typeId] of Object.entries(pendingLeave)) {
      if (isWorkDay(ds)) impact[typeId] = (impact[typeId] || 0) + 1;
    }
    return impact;
  }, [pendingLeave, isWorkDay]);

  const hasChanges = Object.keys(pendingSched).length > 0 || Object.keys(removeSched).length > 0 || Object.keys(pendingLeave).length > 0;

  // ── Clear a day of all pending changes ──
  const clearDay = (ds) => {
    setPendingSched((p) => { const c = { ...p }; delete c[ds]; return c; });
    setPendingLeave((p) => { const c = { ...p }; delete c[ds]; return c; });
    const savedSched = data?.schedule?.[ds];
    if (savedSched) {
      setRemoveSched((p) => ({ ...p, [ds]: true }));
    } else {
      setRemoveSched((p) => { const c = { ...p }; delete c[ds]; return c; });
    }
  };

  // ── Click handler ──
  const handleDayClick = (ds) => {
    if (!data || !isPlannable(ds) || ds < todayStr) return;
    // Can't touch existing server leave
    const existing = (data.leaveRequests || []).find(
      (r) => r.startDate <= ds && r.endDate >= ds && (r.status === 'approved' || r.status === 'pending')
    );
    if (existing) return;

    const info = getDayInfo(ds);

    if (selectedTool === 'office') {
      clearDay(ds);
    } else if (isWorkLocationId(selectedTool)) {
      if (info.type === `pending-${selectedTool}` || info.type === `saved-${selectedTool}`) {
        clearDay(ds);
      } else {
        clearDay(ds);
        setPendingSched((p) => ({ ...p, [ds]: selectedTool }));
      }
    } else {
      // Leave type
      if (info.type === 'pending-leave' && info.typeId === selectedTool) {
        clearDay(ds); // toggle off
      } else {
        clearDay(ds);
        setPendingLeave((p) => ({ ...p, [ds]: selectedTool }));
      }
    }
  };

  // ── Right-click for multi-day leave ──
  const handleDayContext = (e, ds) => {
    e.preventDefault();
    if (!data || ds < todayStr || !isPlannable(ds)) return;
    const existing = (data.leaveRequests || []).find(
      (r) => r.startDate <= ds && r.endDate >= ds && (r.status === 'approved' || r.status === 'pending')
    );
    if (existing) return;
    // Pre-fill the leave form only for leave-type tools; work-location tools
    // (and 'office') aren't leave.
    const isScheduleTool = selectedTool === 'office' || isWorkLocationId(selectedTool);
    const typeId = isScheduleTool ? '' : selectedTool;
    setLeaveForm({ typeId, startDate: ds, endDate: ds, reason: '' });
    setLeaveModal(true);
  };

  const handleAddLeave = () => {
    if (!leaveForm.typeId || !leaveForm.startDate || !leaveForm.endDate) return;
    const newLeave = {};
    const d = new Date(leaveForm.startDate);
    const end = new Date(leaveForm.endDate);
    while (d <= end) {
      const ds = d.toISOString().substring(0, 10);
      // Include weekend days in the visual range so a Wed-Sat leave
      // spans the whole week on the calendar. The backend's
      // calculateWorkingDays still only counts the workdays for
      // balance — visualizing the weekend just shows the time off.
      if (isPlannable(ds) && ds >= todayStr) newLeave[ds] = leaveForm.typeId;
      d.setDate(d.getDate() + 1);
    }
    setPendingSched((p) => {
      const c = { ...p };
      Object.keys(newLeave).forEach((k) => delete c[k]);
      return c;
    });
    setPendingLeave((p) => ({ ...p, ...newLeave }));
    setLeaveModal(null);
  };

  // ── Group pending leave into contiguous requests ──
  // Groups any contiguous plannable days (weekend included). The
  // backend only counts workdays toward balance, but the leave
  // request's date range should match what the user actually drew on
  // the calendar so the Outlook event spans the full stretch.
  const buildGrouped = useCallback(() => {
    const sorted = Object.entries(pendingLeave).sort(([a], [b]) => a.localeCompare(b));
    if (!sorted.length) return [];
    const groups = [];
    let cur = null;
    for (const [ds, typeId] of sorted) {
      if (!isPlannable(ds)) continue;
      if (cur && cur.typeId === typeId && isNextPlannableDay(cur.endDate, ds)) {
        cur.endDate = ds;
      } else {
        cur = { typeId, startDate: ds, endDate: ds, reason: '' };
        groups.push(cur);
      }
    }
    return groups;
  }, [pendingLeave, isPlannable]); // eslint-disable-line react-hooks/exhaustive-deps

  const isNextPlannableDay = (a, b) => {
    const d = new Date(a);
    d.setDate(d.getDate() + 1);
    while (d.toISOString().substring(0, 10) < b) {
      if (isPlannable(d.toISOString().substring(0, 10))) return false;
      d.setDate(d.getDate() + 1);
    }
    return d.toISOString().substring(0, 10) === b;
  };

  // ── Checkout ──
  const handleSave = () => {
    setCheckoutRequests(buildGrouped());
    setShowCheckout(true);
  };

  const updateCheckout = (i, field, val) => {
    setCheckoutRequests((p) => p.map((r, j) => j === i ? { ...r, [field]: val } : r));
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      // Save work-location schedule changes (grouped by type), then removals.
      const schedByType = {};
      for (const [d, t] of Object.entries(pendingSched)) (schedByType[t] = schedByType[t] || []).push(d);
      const removeDates = Object.keys(removeSched);

      for (const [t, ds] of Object.entries(schedByType)) {
        if (ds.length) await employeeApi.saveSchedule(ds, t);
      }
      if (removeDates.length) await employeeApi.saveSchedule(removeDates, 'office');

      // Submit leave requests. Each submit passes suppressAck so it does NOT
      // email the submitter individually — this is one "cart", so we send a
      // single consolidated confirmation afterwards (below) instead of one
      // email per request.
      let leaveOk = 0;
      let warnings = [];
      const submittedLeave = [];
      // One id for the whole cart so the backend can coalesce this checkout's
      // outcome into a single email (one confirmation, plus one "not approved"
      // section) instead of one email per request.
      const batchId =
        (typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      for (const req of checkoutRequests) {
        try {
          const res = await employeeApi.submitRequest({
            typeId: req.typeId,
            startDate: req.startDate,
            endDate: req.endDate,
            reason: req.reason || undefined,
            suppressAck: true,
            batchId,
          });
          leaveOk++;
          submittedLeave.push({
            typeName: getTypeName(req.typeId),
            startDate: req.startDate,
            endDate: req.endDate,
            days: countDays(req.startDate, req.endDate),
            status: res?.request?.status || 'pending',
          });
          if (res.shortNotice) {
            warnings.push(`${getTypeName(req.typeId)} (${req.startDate}): short notice`);
          }
        } catch (err) {
          toast.error(`Leave failed: ${err.message}`);
        }
      }

      // One consolidated confirmation email for the whole cart — but only
      // when it contains leave that needs approval (work-location days never
      // emailed before, so a pure-schedule cart stays email-free). When
      // there IS leave, we list any work-location days alongside it for a
      // complete picture. Fire-and-forget — a mail hiccup must never fail
      // the checkout.
      if (submittedLeave.length) {
        const scheduleSummary = Object.entries(schedByType)
          .filter(([t, ds]) => t !== 'office' && ds.length)
          .map(([t, ds]) => ({ label: wlById[t]?.label || t, count: ds.length }));
        employeeApi.submitBatchAck({ leave: submittedLeave, schedule: scheduleSummary }).catch(() => {});
      }

      // Pure cancellations (removed without a replacement) — office-only conversions
      const cancelledOnly = removeDates.filter((d) => !(d in pendingSched));
      // Changes (replaced saved WFH/Remote with a different value)
      const changedCount = removeDates.filter((d) => d in pendingSched).length;

      const parts = [];
      for (const [t, ds] of Object.entries(schedByType)) {
        if (ds.length) parts.push(`${ds.length} ${wlById[t]?.label || t}`);
      }
      if (cancelledOnly.length) parts.push(`${cancelledOnly.length} cancelled`);
      if (changedCount) parts.push(`${changedCount} changed`);
      if (leaveOk) parts.push(`${leaveOk} leave request${leaveOk !== 1 ? 's' : ''} submitted`);
      toast.success(parts.join(', ') || 'Plan saved');
      if (warnings.length) toast.warning(warnings.join('; '));

      setShowCheckout(false);
      reload({ clearPending: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPendingSched({});
    setRemoveSched({});
    setPendingLeave({});
  };

  const toDateStr = (day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const fmtDate = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const countDays = (s, e) => {
    let n = 0;
    const d = new Date(s);
    while (d <= new Date(e)) {
      if (isWorkDay(d.toISOString().substring(0, 10))) n++;
      d.setDate(d.getDate() + 1);
    }
    return n;
  };

  // Handy totals for the floating action bar (per work-location type).
  const schedCounts = Object.values(pendingSched).reduce((m, t) => { m[t] = (m[t] || 0) + 1; return m; }, {});
  const cancelledOnlyCount = Object.keys(removeSched).filter((d) => !(d in pendingSched)).length;
  const changedCount = Object.keys(removeSched).filter((d) => d in pendingSched).length;
  const leaveCount = Object.keys(pendingLeave).length;

  return (
    <div className="space-y-4 pb-24">
      {/* ── Header: title + quick actions ────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Planner</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Click a day to apply the selected tool. Right-click for multi-day leave.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Subscribe dropdown */}
          <div className="relative">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowSubscribe((s) => !s)}
              className="h-9"
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              Subscribe
              <ChevronDown className={cn('h-3 w-3 transition-transform', showSubscribe && 'rotate-180')} />
            </Button>
            {showSubscribe && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowSubscribe(false)} />
                <div className="absolute right-0 top-full z-40 mt-1 w-[360px]">
                  <CalendarSubscribeCard
                    title="Subscribe in Outlook"
                    description="Add this feed to Outlook so your approved leave, WFH, and Remote days appear on your calendar."
                    fetchUrl={employeeApi.getCalendarUrl}
                    rotateUrl={employeeApi.rotateCalendarUrl}
                  />
                </div>
              </>
            )}
          </div>

          {/* Help popover trigger — top right of the page header */}
          <div className="relative">
            <button
              onClick={() => setShowHelp((s) => !s)}
              className={cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                showHelp
                  ? 'border-primary/50 bg-primary/10 text-primary'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
              title="How to use the planner"
              aria-label="How to use the planner"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            {showHelp && (() => {
              const activeWl = wlById[selectedTool];
              const activeLabel =
                activeWl?.label ||
                (data?.leaveTypes || []).find((t) => t.rowKey === selectedTool)?.displayName ||
                'a tool';
              const activeColor = activeWl?.color || getColor(selectedTool);
              return (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowHelp(false)} />
                  <div className="absolute right-0 top-full z-40 mt-1 w-[320px] rounded-lg border border-border bg-card p-3 shadow-lg">
                    <div className="mb-2 flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">How the planner works</span>
                    </div>
                    <ol className="space-y-2 text-xs text-muted-foreground">
                      <li className="flex gap-2">
                        <span className="shrink-0 font-bold text-foreground">1.</span>
                        <span>
                          Pick a tool from the toolbar below.
                          <span className="ml-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium align-middle" style={{ borderColor: activeColor + '80', background: activeColor + '1A', color: activeColor }}>
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: activeColor }} />
                            {activeLabel}
                          </span>
                          <span className="ml-1">is currently selected.</span>
                        </span>
                      </li>
                      <li className="flex gap-2">
                        <span className="shrink-0 font-bold text-foreground">2.</span>
                        <span>Click any day on the calendar to apply it.</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="shrink-0 font-bold text-foreground">3.</span>
                        <span>Right-click a day to open the multi-day leave form.</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="shrink-0 font-bold text-foreground">4.</span>
                        <span>Hit <span className="font-semibold text-foreground">Save Plan</span> at the bottom when you're done.</span>
                      </li>
                    </ol>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Toolbar: month nav (left) + tool picker + help (far right) ── */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
        {/* Month nav */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMonthOffset((o) => o - 1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex h-8 min-w-[150px] items-center justify-center gap-2 text-sm font-semibold text-foreground">
            {monthLabel}
            {refreshing && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          </div>
          <button
            onClick={() => setMonthOffset((o) => o + 1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => setMonthOffset(0)}
            className="ml-1 inline-flex h-8 items-center rounded-md border border-border px-3 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            Today
          </button>
        </div>

        {/* Tool picker — uniform grid of equal-width pills */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tool</span>
          {(() => {
            const tools = [
              ...activeLocations.map((w) => ({ id: w.id, label: w.label, color: w.color })),
              ...(data?.leaveTypes || []).map((t) => ({ id: t.rowKey, label: t.displayName, color: getColor(t.rowKey) })),
            ];
            // Pick a column count that keeps rows balanced (no stragglers
            // on the last row unless unavoidable) and keeps pills readable.
            const n = tools.length;
            let cols = 4;
            if (n <= 3) cols = n;
            else if (n === 4) cols = 4;
            else if (n === 5 || n === 6) cols = 3;
            else if (n === 7 || n === 8) cols = 4;
            else cols = 5;
            const colsClass = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5' }[cols];
            return (
              <div className={cn('grid gap-1.5', colsClass)}>
                {tools.map((t) => {
                  const active = selectedTool === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTool(t.id)}
                      className={cn(
                        'flex h-8 w-full items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
                        active
                          ? 'text-foreground'
                          : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                      style={active ? { borderColor: t.color + '80', background: t.color + '1A', color: t.color } : undefined}
                    >
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: t.color }} />
                      <span className="truncate">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Admin: edit work-location categories — pinned to the top-right. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowCategoriesEditor(true)}
            className="ml-auto inline-flex items-center gap-1.5 self-start rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Edit work-location categories (admin only)"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit categories
          </button>
        )}
      </div>

      {/* ── Calendar (full width) ───────────────────────────────────── */}
      <Card>
        <CardContent className="pt-4">
              {loading && !data ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className={cn(refreshing && 'opacity-60 transition-opacity')}>
                  <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
                    {DAY_NAMES.map((d) => (
                      <div key={d} className="bg-muted/50 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>
                    ))}
                    {Array.from({ length: startDay }).map((_, i) => <div key={`e-${i}`} className="min-h-[90px] bg-card/30" />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const ds = toDateStr(day);
                      const info = getDayInfo(ds);
                      const isToday = isCurrentMonth && day === todayDate;
                      const isPast = ds < todayStr;
                      // Only holidays are click-locked now. Weekend
                      // days fall through to the same interaction as
                      // weekdays (just visually darker).
                      const canClick = !isPast && info.type !== 'holiday';
                      // Show the clear (X) button for anything we can cancel:
                      // unsaved drafts AND already-saved WFH/Remote days.
                      // Existing leave requests are cancelled from the request detail page, not here.
                      const hasPending = info.type.startsWith('pending-') || info.type.startsWith('saved-');

                      let bg = 'bg-card';
                      let label = null;
                      let labelColor = null;
                      let labelBg = null;
                      let icon = null;

                      if (info.type === 'holiday') {
                        bg = 'bg-muted/30';
                        label = holidayMap[ds];
                        labelColor = '#F85707';
                        labelBg = 'rgba(248,87,7,0.12)';
                      } else if (info.type === 'weekend') {
                        // Weekend resting state — darker grey than a
                        // weekday "office" cell, but still clickable.
                        bg = 'bg-muted/60';
                      } else if ((info.type.startsWith('pending-') || info.type.startsWith('saved-')) && info.type !== 'pending-leave') {
                        const wlId = info.type.replace(/^(pending|saved)-/, '');
                        const c = wlById[wlId]?.color || '#64748b';
                        icon = <MapPin className="h-2.5 w-2.5" />;
                        label = wlById[wlId]?.label || wlId;
                        labelColor = c;
                        labelBg = c + '1F';
                      } else if (info.type === 'pending-leave') {
                        const c = getColor(info.typeId);
                        bg = `bg-card`;
                        label = `${getTypeName(info.typeId)} *`;
                        labelColor = c;
                        labelBg = c + '18';
                      } else if (info.type === 'existing-leave') {
                        const c = getColor(info.typeId);
                        if (info.status === 'approved') {
                          bg = 'bg-card';
                          label = info.typeName || getTypeName(info.typeId);
                          labelColor = c;
                          labelBg = c + '18';
                        } else if (info.status === 'pending') {
                          bg = 'bg-warning/5';
                          label = `${info.typeName || getTypeName(info.typeId)} (pending)`;
                          labelColor = '#B45309';
                          labelBg = 'rgba(180,83,9,0.1)';
                        } else {
                          label = `${info.typeName || getTypeName(info.typeId)} (${info.status})`;
                          labelColor = '#6B7280';
                          labelBg = 'rgba(107,114,128,0.1)';
                        }
                      }

                      return (
                        <div
                          key={day}
                          onClick={() => canClick && handleDayClick(ds)}
                          onContextMenu={(e) => canClick && handleDayContext(e, ds)}
                          className={cn(
                            'min-h-[90px] p-1.5 transition-colors relative',
                            bg,
                            isToday && 'ring-2 ring-inset ring-primary',
                            canClick && 'cursor-pointer hover:bg-accent/40',
                            isPast && 'opacity-40',
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <span className={cn('text-[11px] font-medium', isToday ? 'text-primary font-bold' : 'text-muted-foreground')}>{day}</span>
                            {hasPending && !isPast && (
                              <button onClick={(e) => { e.stopPropagation(); clearDay(ds); }} className="rounded-full p-0.5 text-destructive hover:bg-destructive/10 transition-colors">
                                <X className="h-2.5 w-2.5" />
                              </button>
                            )}
                          </div>
                          {label && (
                            <div className="flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-medium mt-0.5 truncate" style={{ background: labelBg, color: labelColor }}>
                              {icon}{label}
                            </div>
                          )}
                          {info.type === 'office' && !isPast && (
                            <Building2 className="h-2.5 w-2.5 text-muted-foreground/20 mt-1" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Click to apply &middot; Right-click for multi-day leave &middot; X on a day to clear it
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

      {/* ── Floating action bar (only when hasChanges) ──────────────── */}
      {hasChanges && (
        <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 w-[min(calc(100vw-2rem),700px)]">
          <div className="flex items-center gap-3 rounded-full border border-primary/30 bg-card/95 px-4 py-2 shadow-lg backdrop-blur">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">Unsaved</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {Object.entries(schedCounts).map(([t, n]) => (
                <span key={t} className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: wlById[t]?.color || '#64748b' }} />
                  {n} {wlById[t]?.label || t}
                </span>
              ))}
              {cancelledOnlyCount > 0 && (
                <span className="inline-flex items-center gap-1"><X className="h-3 w-3 text-destructive" />{cancelledOnlyCount} cancelled</span>
              )}
              {changedCount > 0 && (
                <span className="inline-flex items-center gap-1"><AlertCircle className="h-3 w-3 text-profun-amber" />{changedCount} changed</span>
              )}
              {leaveCount > 0 && (
                <span className="inline-flex items-center gap-1"><Palmtree className="h-3 w-3 text-profun-blue" />{leaveCount} leave day{leaveCount !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="ml-auto flex gap-1.5">
              <Button variant="ghost" size="sm" onClick={handleDiscard} className="h-7">Discard</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} loading={saving} className="h-7">
                <Save className="h-3.5 w-3.5" /> Save Plan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-day leave modal */}
      <Modal isOpen={!!leaveModal} onClose={() => setLeaveModal(null)} title="Plan Leave" width="420px">
        <div className="space-y-4">
          <Select label="Leave Type" value={leaveForm.typeId} onChange={(e) => setLeaveForm((f) => ({ ...f, typeId: e.target.value }))} options={[{ value: '', label: 'Select...' }, ...(data?.leaveTypes || []).map((t) => ({ value: t.rowKey, label: t.displayName }))]} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start" type="date" value={leaveForm.startDate} onChange={(e) => setLeaveForm((f) => ({ ...f, startDate: e.target.value }))} />
            <Input label="End" type="date" value={leaveForm.endDate} onChange={(e) => setLeaveForm((f) => ({ ...f, endDate: e.target.value }))} />
          </div>
          <Input label="Reason (optional)" value={leaveForm.reason} onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Family vacation" />
          <Button onClick={handleAddLeave} disabled={!leaveForm.typeId || !leaveForm.startDate || !leaveForm.endDate} className="w-full">Add to Plan</Button>
        </div>
      </Modal>

      {/* Checkout modal */}
      <Modal isOpen={showCheckout} onClose={() => !saving && setShowCheckout(false)} title="Review & Submit Plan" width="600px">
        <div className="space-y-5">
          {/* WFH/Remote summary */}
          {(Object.keys(pendingSched).length > 0 || Object.keys(removeSched).length > 0) && (() => {
            const cancelledOnlyDates = Object.keys(removeSched).filter((d) => !(d in pendingSched)).sort();
            const changedDates = Object.keys(removeSched).filter((d) => d in pendingSched).sort();
            const totalDays = Object.keys(pendingSched).length + cancelledOnlyDates.length;
            return (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Home className="h-4 w-4 text-profun-teal" /> Schedule Changes
                  <Badge variant="default">{totalDays} day{totalDays !== 1 ? 's' : ''}</Badge>
                </h4>
                <div className="rounded-lg bg-muted/30 border border-border p-3 space-y-2">
                  {[...new Set(Object.values(pendingSched))].map((type) => {
                    const dates = Object.entries(pendingSched).filter(([, t]) => t === type).map(([d]) => d).sort();
                    if (!dates.length) return null;
                    const c = wlById[type]?.color || '#64748b';
                    return (
                      <div key={type}>
                        <div className="text-xs font-medium text-muted-foreground mb-1">
                          {wlById[type]?.label || type}
                          {changedDates.some((d) => pendingSched[d] === type) && (
                            <span className="ml-2 text-[10px] text-profun-amber">(includes changes)</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {dates.map((d) => (
                            <span key={d} className="rounded-md px-2 py-0.5 text-xs font-medium" style={{ background: c + '26', color: c }}>
                              {fmtDate(d)}
                              {d in removeSched && <span className="ml-1 opacity-60">↻</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {cancelledOnlyDates.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">Cancelled (back to office)</div>
                      <div className="flex flex-wrap gap-1.5">
                        {cancelledOnlyDates.map((d) => (
                          <span key={d} className="rounded-md px-2 py-0.5 text-xs font-medium bg-destructive/15 text-destructive line-through">
                            {fmtDate(d)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground">No approval needed — saved immediately.</p>
                </div>
              </div>
            );
          })()}

          {/* Leave requests */}
          {checkoutRequests.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Palmtree className="h-4 w-4 text-profun-blue" /> Leave Requests
                <Badge variant="default">{checkoutRequests.length}</Badge>
              </h4>
              <div className="space-y-3">
                {checkoutRequests.map((req, i) => {
                  const c = getColor(req.typeId);
                  // Fall back to the type's default allowance when the user
                  // has no balance row yet (new hire, new leave type, etc.)
                  // — otherwise we'd show "—" and a negative "after" value
                  // that doesn't reflect reality.
                  const type = (data?.leaveTypes || []).find((t) => t.rowKey === req.typeId);
                  const fallbackAllowance = type?.defaultAllowance || 0;
                  const bal = data?.balances?.[req.typeId] || {
                    allowance: fallbackAllowance,
                    used: 0,
                    pending: 0,
                    remaining: fallbackAllowance,
                  };
                  const remaining = bal.remaining ?? fallbackAllowance;
                  const days = countDays(req.startDate, req.endDate);
                  const after = remaining - days;
                  const single = req.startDate === req.endDate;
                  return (
                    <div key={i} className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full" style={{ background: c }} />
                          <span className="text-sm font-semibold" style={{ color: c }}>{getTypeName(req.typeId)}</span>
                        </div>
                        <Badge variant="info">{days} day{days !== 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{single ? fmtDate(req.startDate) : `${fmtDate(req.startDate)} → ${fmtDate(req.endDate)}`}</div>
                      <Input label="Reason" value={req.reason} onChange={(e) => updateCheckout(i, 'reason', e.target.value)} placeholder="e.g. Family vacation..." />
                      {type?.entitlementState && (
                        <div className={cn(
                          'text-[11px]',
                          !type.entitlementState.eligible ? 'text-destructive'
                            : type.entitlementState.remaining > 0 ? 'text-muted-foreground' : 'text-warning'
                        )}>
                          {!type.entitlementState.eligible
                            ? type.entitlementState.reason
                            : `${type.entitlementState.remaining} of ${type.entitlementState.amount} left this ${type.entitlementState.period}`}
                        </div>
                      )}
                      {type?.countsAgainstBalance === false ? (
                        <div className="text-[11px] text-muted-foreground">No leave-balance impact</div>
                      ) : (
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 tabular-nums">
                          Balance: <span className="font-medium text-foreground">{remaining}</span>
                          → <span className={cn('font-medium', after < 0 ? 'text-destructive' : 'text-profun-teal')}>{after}</span> remaining
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Each request goes to your manager for approval.</p>
            </div>
          )}

          {Object.keys(pendingSched).length === 0 && Object.keys(removeSched).length === 0 && checkoutRequests.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No changes to submit.</p>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <Button variant="ghost" onClick={() => setShowCheckout(false)} disabled={saving}>Back</Button>
            <Button onClick={handleConfirm} loading={saving} disabled={saving}>
              <Send className="h-4 w-4" />
              {checkoutRequests.length > 0 ? 'Submit for Approval' : 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>

      {isAdmin && showCategoriesEditor && (
        <WorkLocationsEditor
          current={workLocations}
          onClose={() => setShowCategoriesEditor(false)}
          onSaved={() => { setShowCategoriesEditor(false); reload({ silent: true }); }}
        />
      )}
    </div>
  );
}

// Admin-only editor for the Planner's work-location categories. Saves the
// list to global Settings (workLocations) via the merge-update endpoint, so
// every consumer (planner, calendar, ICS, team views) reads from one place.
function WorkLocationsEditor({ current, onClose, onSaved }) {
  const toast = useToast();
  const [rows, setRows] = useState(() => (current || []).map((w) => ({ ...w })));
  const [saving, setSaving] = useState(false);

  const set = (i, field, value) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [field]: value } : r)));
  const addRow = () => setRows((p) => [...p, { id: '', label: '', color: '#3985FD', active: true, _new: true }]);
  const removeRow = (i) => setRows((p) => p.filter((_, j) => j !== i));

  const slug = (s) => String(s).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);

  const handleSave = async () => {
    const cleaned = [];
    const seen = new Set();
    for (const r of rows) {
      const label = (r.label || '').trim();
      if (!label) continue;
      const id = (r._new || !r.id) ? slug(label) : r.id;
      if (!id || id === 'office' || seen.has(id)) continue;
      seen.add(id);
      cleaned.push({ id, label, color: r.color || '#64748b', active: r.active !== false });
    }
    if (!cleaned.length) { toast.error('Add at least one category.'); return; }
    setSaving(true);
    try {
      await adminApi.updateSettings({ workLocations: cleaned });
      toast.success('Planner categories updated.');
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit planner work-location categories" width="560px">
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          These are the instant, no-approval options employees pick in the Planner to say where
          they are working (e.g. Remote, Off Site). Leave types and approvals are managed in
          Admin then Leave Types.
        </p>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-border bg-card p-2">
              <input
                type="color"
                value={r.color || '#64748b'}
                onChange={(e) => set(i, 'color', e.target.value)}
                className="h-8 w-8 shrink-0 cursor-pointer rounded border border-border bg-transparent"
                title="Color"
              />
              <Input
                value={r.label}
                onChange={(e) => set(i, 'label', e.target.value)}
                placeholder="Category name (e.g. Client Site)"
                className="flex-1"
              />
              <label className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                <input type="checkbox" checked={r.active !== false} onChange={(e) => set(i, 'active', e.target.checked)} />
                Active
              </label>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                title="Remove"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addRow}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-xs font-medium text-muted-foreground transition-all hover:border-primary/40 hover:bg-muted/30 hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" /> Add category
        </button>
        <p className="text-[11px] text-muted-foreground">
          Tip: uncheck Active to retire a category without losing past entries. Days already
          logged under a category stay on the calendar.
        </p>
        <div className="flex justify-end gap-3 border-t border-border pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} disabled={saving}>Save categories</Button>
        </div>
      </div>
    </Modal>
  );
}
